import * as PagePaths from '../constants/pagePath';
import { ACCESS_MATRIX, RouteGroups } from '../constants/accessMetrix';
import { SubscriptionPlan } from '../helpers/enums';
import { emailFromUser } from '../helpers/authUtils';

export const calculateRouteAccess = (
  routePath,
  user,
  options = {}
) => {
  const systemAdminAllowed =
    options.systemAdminAllowed !== undefined ? options.systemAdminAllowed : null;
  const forMenu = options.forMenu === true;
  const profileInformation = options.profileInformation || null;
  // Check if user has active organization membership and is not on Free plan
  // Organization features are only available for Premium (2) and Executive (3) plans
  const hasOrganization =
    user?.organizationId > 0 && (user?.currentPlan === 2 || user?.currentPlan === 3);
  if (user?.id === 3) user.currentPlan = 3;
  const CurrentSubscriptionPlan = user?.currentPlan || 0; // Default to 0 (New) if undefined
  const isAdmin = user?.role === 'Admin';
  // Check for Organization Manager (case-insensitive) or isManager flag
  // Handle various edge cases: string "true", number 1, boolean true, role with whitespace
  const roleIsOrgManager = user?.role && (
    user.role === 'Organization Manager' || 
    user.role.trim().toLowerCase() === 'organization manager'
  );
  const isManagerFlag = 
    user?.isManager === true || 
    user?.IsManager === true ||
    user?.isManager === 1 ||
    user?.IsManager === 1 ||
    String(user?.isManager).toLowerCase() === 'true' ||
    String(user?.IsManager).toLowerCase() === 'true';
  const isOrgManager = roleIsOrgManager || isManagerFlag;

  // Debug log for organization/create route
  if (routePath === PagePaths.ORGANIZATION_CREATE_ORGANIZATION) {
    console.log('calculateRouteAccess - Checking access for /organization/create:', {
      routePath,
      hasOrganization,
      userOrganizationId: user?.organizationId,
      currentPlan: user?.currentPlan,
    });
  }

  if ([PagePaths.HOME_PATH].includes(routePath)) return true;

  if (IdentifyIfRouteIsPublic(routePath)) return true;

  // Lockout page: always mount for authenticated users (PrivateRoute handles auth).
  if (routePath === PagePaths.SUBSCRIPTION_EXPIRED_PATH) {
    return Boolean(user);
  }

  // System admin: menu only when API confirms allow; route mounts for any authed user so SystemAdmin can show denial.
  if (routePath === PagePaths.SYSTEM_ADMIN_PATH) {
    if (!user) return false;
    if (forMenu) return systemAdminAllowed === true;
    return true;
  }

  // Special handling for organization routes to prevent showing both
  if (routePath === PagePaths.ORGANIZATION_ORGANIZATION_PATH && !hasOrganization) {
    return false; // Don't show /organization route if user has no organization
  }

  if (routePath === PagePaths.ORGANIZATION_NON_EXISTENT_ORGANIZATION_PATH && hasOrganization) {
    return false; // Don't show /non-existent-organization route if user has organization
  }

  // Allow non-existent-organization route for users without organization
  if (routePath === PagePaths.ORGANIZATION_NON_EXISTENT_ORGANIZATION_PATH && !hasOrganization) {
    return true; // Show /non-existent-organization route if user has no organization
  }

  // Allow CreateOrganization route for all users (component will handle redirect if user has organization)
  if (
    routePath === PagePaths.ORGANIZATION_CREATE_ORGANIZATION ||
    routePath === PagePaths.ORGANIZATION_REACTIVATE_PATH
  ) {
    // If user has organization, allow access (component will redirect to dashboard)
    if (hasOrganization) {
      return true;
    }
    // If user doesn't have organization, check access permissions
    return evaluateAccess(CurrentSubscriptionPlan, isAdmin, hasOrganization, 'CreateOrganization');
  }

  console.log(
    'CurrentSubscriptionPlan:',
    CurrentSubscriptionPlan,
    'isAdmin:',
    isAdmin,
    'hasOrganization:',
    hasOrganization,
    'isOrgManager:',
    isOrgManager
  );

  // Bulk Operations: org owners/managers on Premium or Executive only.
  // Matrix lookup currently doesn't pass isOrgManager into evaluateAccess,
  // so this short-circuit also documents intent and stays correct under
  // future matrix shuffles.
  //
  // Testing-mode gate: while the BE has BulkImportTesting:Enabled=true (see
  // backend appsettings.json -> BulkImportTesting.AllowedImporterEmail), only
  // the executive can actually invoke any /api/bulk-operations endpoint.
  // Mirror that on the FE so non-executive users don't see a nav link that
  // 403s on every call. Remove this email gate when the BE flag flips to false.
  if (routePath === PagePaths.BULK_OPERATIONS_PATH) {
    if (!user) return false;
    const eligiblePlan =
      CurrentSubscriptionPlan === SubscriptionPlan.Premium ||
      CurrentSubscriptionPlan === SubscriptionPlan.Executive;
    const baseEligible = Boolean(hasOrganization && eligiblePlan && (isAdmin || isOrgManager));
    if (!baseEligible) return false;
    const ALLOWED_BULK_IMPORT_EMAIL = 'executive@prove-it-to.me';
    const emailLc = emailFromUser(user, profileInformation).toLowerCase();
    return emailLc === ALLOWED_BULK_IMPORT_EMAIL;
  }

  for (const group of RouteGroups) {
    if (group.paths.includes(routePath)) {
      const hasAccess = evaluateAccess(
        CurrentSubscriptionPlan,
        isAdmin,
        hasOrganization,
        group.feature
      );
      if (hasAccess) return true;
    }
  }

  // === Always Accessible Special Pages ===
  if (routePath.includes('value-propositions/shared') && !routePath.includes('redirectUrl'))
    return true;
};

export function evaluateAccess(subscription, isAdmin, hasOrganization, feature, isOrgManager = false) {
  const subscriptionNum = Number(subscription);
  const isAdminNormalized = Boolean(isAdmin);
  const isOrgManagerNormalized = Boolean(isOrgManager);
  const hasOrg = Boolean(hasOrganization);
  
  const rule = ACCESS_MATRIX.find(
    (r) =>
      Number(r.subscription) === subscriptionNum && 
      r.isAdmin === isAdminNormalized && 
      r.isOrgManager === isOrgManagerNormalized &&
      r.Organization === hasOrg
  );

  return rule?.permissions?.[feature] || false;
}

export function IdentifyIfRouteIsPublic(routePath) {
  const publicPaths = [
    PagePaths.ACCOUNT_PATH,
    PagePaths.LOGIN_PATH,
    PagePaths.SIGN_UP_PATH,
    PagePaths.LOGOUT_PATH,
    PagePaths.FORGET_PASSWORD_PATH,
    PagePaths.FORGOT_PASSWORD_PATH,
    PagePaths.REGISTER_PATH,
    PagePaths.NOT_FOUND_PATH,
    PagePaths.ERROR_PAGE_PATH,
    PagePaths.PAYMENT_SUCCESS_PATH,
    PagePaths.ORGANIZATION_LICENSE_STRIP_PURCHASE_CANCEL,
    PagePaths.ORGANIZATION_LICENSE_STRIP_PURCHASE_SUCCESS,
    PagePaths.ORGANIZATION_STRIPE_PAYMENT_SUCCESS_PATH,
    PagePaths.ORGANIZATION_STRIPE_PAYMENT_CANCEL_PATH,
    PagePaths.CONFIRM_ORGANIZATION_INVITATION,
    '/profile/shared',
    PagePaths.VP_SHARE_LINK_PREFIX, // the Share Link, matched by prefix like the profile link
  ];

  for (const path of publicPaths) {
    if (routePath && routePath.includes(path)) {
      return true;
    }
  }
}

export function calculateAccessByGroup(
  user,
  subscription,
  role,
  hasOrganization,
  groupKey,
  feature
) {
  // Normalize subscription to number for comparison
  const subscriptionNum = Number(subscription);
  // Normalize role - check for Admin (case-insensitive)
  const isAdmin = role && (role === 'Admin' || role.toLowerCase() === 'admin');
  // Normalize role - check for Organization Manager (case-insensitive)
  // Also check isManager flag from user object (supports both isManager and IsManager)
  // This determines which ACCESS_MATRIX entry to match
  const isOrgManager = 
    (role && (role === 'Organization Manager' || role.toLowerCase() === 'organization manager')) ||
    user?.isManager === true ||
    user?.IsManager === true;
  // Normalize hasOrganization to boolean
  const hasOrg = Boolean(hasOrganization);
  
  // Look up permission in ACCESS_MATRIX - this is the single source of truth
  // ACCESS_MATRIX entries with isOrgManager: true and LicensesDetails: true will grant access
  const rule = ACCESS_MATRIX.find(
    (r) =>
      Number(r.subscription) === subscriptionNum && 
      r.isAdmin === isAdmin && 
      r.isOrgManager === isOrgManager &&
      r.Organization === hasOrg
  );
  // Return the permission from ACCESS_MATRIX (defaults to false if no match)
  const result = rule?.[groupKey]?.[feature] || false;
  return result;
}

export function calculateFeatureAccess(
  user,
  subscription,
  role,
  hasOrganization,
  feature,
  category
) {
  // Normalize subscription to number for comparison
  const subscriptionNum = Number(subscription);
  // Normalize role - check for Admin (case-insensitive)
  const isAdmin = role && (role === 'Admin' || role.toLowerCase() === 'admin');
  // Normalize role - check for Organization Manager (case-insensitive)
  // Also check isManager flag from user object (supports both isManager and IsManager)
  // This determines which ACCESS_MATRIX entry to match
  const isOrgManager = 
    (role && (role === 'Organization Manager' || role.toLowerCase() === 'organization manager')) ||
    user?.isManager === true ||
    user?.IsManager === true;
  // Normalize hasOrganization to boolean
  const hasOrg = Boolean(hasOrganization);
  
  // Look up permission in ACCESS_MATRIX - this is the single source of truth
  const rule = ACCESS_MATRIX.find(
    (r) =>
      Number(r.subscription) === subscriptionNum && 
      r.isAdmin === isAdmin && 
      r.isOrgManager === isOrgManager &&
      r.Organization === hasOrg
  );

  // Return the permission from ACCESS_MATRIX (defaults to false if no match)
  const result = rule?.[category]?.[feature] || false;
  console.log('calculateFeatureAccess:', feature, result, rule, category, user, subscription, role, hasOrganization);
  return result;
}
